import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Button, Card, Spinner, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Database, Download, Upload, AlertTriangle, ShieldCheck, RefreshCw } from "lucide-react";

export default function Backup() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [wipeFirst, setWipeFirst] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [err, setErr] = useState("");

  const loadStats = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/backup/stats");
      setStats(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadStats(); }, []);

  const downloadBackup = async () => {
    setDownloading(true); setErr("");
    try {
      const res = await api.get("/backup/download", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const link = document.createElement("a");
      const cd = res.headers["content-disposition"] || "";
      const filenameMatch = cd.match(/filename="([^"]+)"/);
      link.href = url;
      link.download = filenameMatch ? filenameMatch[1] : `serenata_backup_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { setErr(formatErr(e)); }
    finally { setDownloading(false); }
  };

  const uploadBackup = async (file) => {
    if (!file) return;
    const label = wipeFirst
      ? `⚠️ RESTAURAR APAGANDO TUDO?\n\nMarcado "apagar antes de restaurar" — todas as coleções serão APAGADAS e substituídas pelo backup.\n\nEssa ação é IRREVERSÍVEL. Confirma?`
      : `Restaurar backup fazendo merge (upsert por id)?\n\nDocumentos com mesmo id serão atualizados; novos serão inseridos. Nenhum dado atual será apagado.`;
    if (!window.confirm(label)) return;
    setRestoring(true); setErr(""); setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/backup/restore?wipe_first=${wipeFirst}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setRestoreResult(data);
      await loadStats();
    } catch (e) { setErr(formatErr(e)); }
    finally { setRestoring(false); }
  };

  return (
    <Layout
      title="Backup e Restore"
      subtitle="Baixe uma cópia completa do banco para armazenamento externo — ou restaure a partir de um arquivo."
      actions={
        <Button variant="secondary" onClick={loadStats} disabled={loading} data-testid="backup-refresh">
          <RefreshCw size={14} /> Atualizar
        </Button>
      }
    >
      {err && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-danger text-sm">
          {err}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {/* Stats */}
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Database size={18} className="text-brand-600" />
            <h3 className="font-heading text-lg font-bold text-ink-900">Coleções do banco</h3>
          </div>
          {loading ? (
            <div className="py-10 flex items-center justify-center"><Spinner /></div>
          ) : (
            <>
              <div className="mb-3 text-sm text-ink-700">
                Total: <span className="font-mono font-bold text-brand-600">{fmtNum(stats?.total_documents ?? 0, 0)}</span> documentos em{" "}
                <span className="font-bold">{stats?.collections?.length ?? 0}</span> coleções
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                {(stats?.collections || []).map((c) => (
                  <div key={c.collection} className="flex justify-between items-baseline px-3 py-1.5 rounded-md bg-stone-50 border border-stone-200">
                    <span className="text-ink-700 font-medium truncate">{c.collection}</span>
                    <span className="font-mono text-brand-700 font-bold ml-2">{fmtNum(c.documents, 0)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* Baixar */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <Download size={18} className="text-brand-600" />
            <h3 className="font-heading text-lg font-bold text-ink-900">Baixar backup</h3>
          </div>
          <p className="text-xs text-ink-500 mb-4 leading-relaxed">
            Gera um arquivo <strong>.zip</strong> com todo o banco em formato JSONL + manifest. Guarde em um pendrive, Google Drive ou outro local seguro <strong>fora do servidor</strong>.
          </p>
          <Button onClick={downloadBackup} disabled={downloading} className="w-full" data-testid="backup-download">
            {downloading ? <Spinner size={14} /> : <Download size={14} />} Baixar agora
          </Button>
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-[11px] text-amber-900">
            <strong>Recomendação:</strong> baixe um backup <strong>toda semana</strong> — ou antes de operações críticas (ajuste de estoque, mudança de configuração fiscal, etc.).
          </div>
        </Card>
      </div>

      {/* Restaurar */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-2">
          <Upload size={18} className="text-warn" />
          <h3 className="font-heading text-lg font-bold text-ink-900">Restaurar backup</h3>
        </div>
        <p className="text-xs text-ink-500 mb-4 leading-relaxed">
          Envie um arquivo .zip gerado pelo módulo de backup para restaurar dados. Modo padrão faz <strong>upsert por id</strong> — nada é apagado.
        </p>

        <label className="flex items-start gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={wipeFirst}
            onChange={(e) => setWipeFirst(e.target.checked)}
            className="mt-0.5 accent-red-600"
            data-testid="backup-wipe-toggle"
          />
          <div>
            <div className="text-sm font-semibold text-danger flex items-center gap-1">
              <AlertTriangle size={13} /> Apagar TUDO antes de restaurar
            </div>
            <div className="text-[11px] text-ink-500">Todas as coleções presentes no ZIP serão apagadas antes do restore. Ação IRREVERSÍVEL.</div>
          </div>
        </label>

        <label className={`block border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${wipeFirst ? "border-danger bg-red-50 hover:bg-red-100" : "border-stone-300 bg-stone-50 hover:bg-stone-100"}`}>
          <input
            type="file"
            accept=".zip"
            className="hidden"
            onChange={(e) => uploadBackup(e.target.files?.[0])}
            disabled={restoring}
            data-testid="backup-upload"
          />
          <Upload size={22} className={`mx-auto mb-2 ${wipeFirst ? "text-danger" : "text-ink-500"}`} />
          <div className="text-sm font-semibold text-ink-900">
            {restoring ? "Restaurando..." : "Clique para escolher um .zip de backup"}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">
            {wipeFirst ? "⚠️ Modo DESTRUTIVO ativado" : "Modo seguro (merge por id)"}
          </div>
        </label>

        {restoreResult && (
          <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-md">
            <div className="flex items-center gap-2 text-emerald-900 font-semibold mb-2">
              <ShieldCheck size={16} /> Restore concluído
            </div>
            <div className="text-xs text-ink-700 space-y-0.5">
              <div>Total inserido: <span className="font-mono font-bold">{fmtNum(restoreResult.resumo.total_inserted, 0)}</span></div>
              <div>Total atualizado: <span className="font-mono font-bold">{fmtNum(restoreResult.resumo.total_updated, 0)}</span></div>
              <details className="mt-2">
                <summary className="cursor-pointer text-brand-600 font-semibold text-xs">Detalhes por coleção</summary>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1 text-[11px]">
                  {Object.entries(restoreResult.resumo.collections).map(([name, r]) => (
                    <div key={name} className="flex justify-between px-2 py-1 rounded bg-white border">
                      <span className="truncate">{name}</span>
                      <span className="font-mono ml-2 text-brand-600">+{r.inserted}/~{r.updated}</span>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          </div>
        )}
      </Card>
    </Layout>
  );
}
